// CreditNova Metrics Selector Component
// Allows users to select financial ratios for analysis
import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CustomRatio, FINANCIAL_METRICS } from '@creditnova/shared/schemas';
import { 
  Calculator, 
  TrendingUp, 
  Scale, 
  Percent, 
  BarChart3,
  Plus
} from 'lucide-react';
import { FormulaBuilder } from './FormulaBuilder';

// ===========================================
// Types
// ===========================================

interface MetricsSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMetrics: string[];
  initialCustomRatios?: CustomRatio[];
  onSave: (metrics: string[], customRatios: CustomRatio[]) => void;
  onAddCustomRatio?: () => void;
  availableFields?: string[];
}

interface MetricCategory {
  name: string;
  icon: React.ElementType;
  description: string;
}

// ===========================================
// Category Configuration
// ===========================================

const categoryConfig: Record<string, MetricCategory> = {
  Liquidity: {
    name: 'Liquidity',
    icon: Scale,
    description: 'Measures ability to meet short-term obligations',
  },
  Profitability: {
    name: 'Profitability',
    icon: TrendingUp,
    description: 'Measures ability to generate earnings',
  },
  Leverage: {
    name: 'Leverage',
    icon: BarChart3,
    description: 'Measures use of debt financing',
  },
  Efficiency: {
    name: 'Efficiency',
    icon: Percent,
    description: 'Measures operational efficiency',
  },
  Custom: {
    name: 'Custom',
    icon: Plus,
    description: 'User-defined financial ratios and calculations',
  },
};

// ===========================================
// CategoryTab Component
// ===========================================

interface MetricItem {
  id: string;
  name: string;
  category: string;
  formula: string;
}

interface CategoryTabProps {
  category: string;
  metrics: MetricItem[];
  selectedMetrics: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

function CategoryTab({ 
  category, 
  metrics, 
  selectedMetrics, 
  onToggle,
  onSelectAll,
  onDeselectAll 
}: CategoryTabProps) {
  const categoryMetrics = metrics.filter(m => m.category === category);
  const selectedCount = categoryMetrics.filter(m => selectedMetrics.has(m.id)).length;
  const allSelected = selectedCount === categoryMetrics.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {categoryConfig[category]?.description}
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={allSelected ? onDeselectAll : onSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        {categoryMetrics.map((metric) => (
          <div
            key={metric.id}
            className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => onToggle(metric.id)}
          >
            <Checkbox
              checked={selectedMetrics.has(metric.id)}
              onCheckedChange={() => onToggle(metric.id)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{metric.name}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {metric.formula}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================
// MetricsSelector Main Component
// ===========================================

export function MetricsSelector({
  open,
  onOpenChange,
  selectedMetrics: initialSelectedMetrics,
  initialCustomRatios = [],
  onSave,
  onAddCustomRatio,
  availableFields = [],
}: MetricsSelectorProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    new Set(initialSelectedMetrics)
  );
  const [activeCategory, setActiveCategory] = useState('Liquidity');

  // State for Formula Builder
  const [isFormulaBuilderOpen, setIsFormulaBuilderOpen] = useState(false);

  // Initialize from props (template load / saved ratios) or empty
  const [customRatios, setCustomRatios] = useState<CustomRatio[]>(initialCustomRatios);

  // Group metrics by category
  const metricsByCategory = useMemo(() => {
    const grouped: Record<string, MetricItem[]> = {};
    // Group Standard Metrics
    FINANCIAL_METRICS.forEach((metric) => {
      if (!grouped[metric.category]) grouped[metric.category] = [];
      grouped[metric.category].push(metric as MetricItem);
    });

    // Ensure the "Custom" key exists even if empty, so the tab shows up
    if (!grouped['Custom']) {
      grouped['Custom'] = [];
    }

    customRatios.forEach(r => {
      grouped['Custom'].push({
        id: r.id,
        name: r.name,
        category: 'Custom',
        formula: r.formula
      });
    });
    
    return grouped;
  }, [customRatios]);

  const categories = Object.keys(metricsByCategory);

  const handleToggle = (id: string) => {
    const newSet = new Set(selectedMetrics);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedMetrics(newSet);
  };

  // Handle saving from the FormulaBuilder
  const handleAddCustomRatio = (newRatio: Omit<CustomRatio, 'id'>) => {
    // 1. Create a unique ID for the new custom ratio
    const uuid = crypto.randomUUID();
    const customId = `custom_${uuid}`;

    const ratioWithId: CustomRatio = {
      ...newRatio,
      id: customId,
    };
    
    // 2. Update the DEFINITIONS
    setCustomRatios(prev => [...prev, ratioWithId]);
    
    // 3. Update the SELECTION (The checkboxes)
    // Use the functional update to ensure you have the latest set
    setSelectedMetrics(prev => {
      const newSet = new Set(prev);
      newSet.add(customId);
      return newSet;
    });
    
    setIsFormulaBuilderOpen(false);
  };

  const handleSelectAllInCategory = (category: string) => {
    const newSet = new Set(selectedMetrics);
    metricsByCategory[category].forEach((m) => newSet.add(m.id));
    setSelectedMetrics(newSet);
  };

  const handleDeselectAllInCategory = (category: string) => {
    const newSet = new Set(selectedMetrics);
    metricsByCategory[category].forEach((m) => newSet.delete(m.id));
    setSelectedMetrics(newSet);
  };

  const handleSave = () => {
   const selectedIdsArray = Array.from(selectedMetrics);
    // FILTER: Only send definitions for ratios that are actually checked
    const activeCustomDefinitions = customRatios.filter(ratio => 
      selectedMetrics.has(ratio.id)
    );

    onSave(selectedIdsArray, activeCustomDefinitions);
    onOpenChange(false);
  };

  const totalSelected = selectedMetrics.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Select Financial Metrics
          </DialogTitle>
          <DialogDescription>
            Choose the financial ratios to include in your analysis. 
            Selected: {totalSelected} of {FINANCIAL_METRICS.length} metrics
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mt-4">
          <TabsList className="grid grid-cols-5 w-full">
            {categories.map((category) => {
              const Icon = categoryConfig[category]?.icon || Calculator;
              const categoryCount = metricsByCategory[category]?.filter(
                (m) => selectedMetrics.has(m.id)
              ).length || 0;
              
              return (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  className="relative"
                >
                  <Icon className="w-4 h-4 mr-1.5" />
                  {category}
                  {categoryCount > 0 && (
                    <Badge 
                      variant="secondary" 
                      className="ml-1.5 h-5 px-1.5 text-xs"
                    >
                      {categoryCount}
                    </Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <ScrollArea className="h-[350px] mt-4 pr-4">
            {categories.map((category) => (
              <TabsContent key={category} value={category} className="mt-0">
                <CategoryTab
                  category={category}
                  metrics={metricsByCategory[category] as MetricItem[]}
                  selectedMetrics={selectedMetrics}
                  onToggle={handleToggle}
                  onSelectAll={() => handleSelectAllInCategory(category)}
                  onDeselectAll={() => handleDeselectAllInCategory(category)}
                />
                {category === 'Custom' && (
                  <div className="mt-6 flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl bg-muted/30">
                    <p className="text-sm text-muted-foreground mb-4 text-center">
                      Need a specific calculation? Build your own financial ratio.
                    </p>
                    <Button 
                      onClick={() => setIsFormulaBuilderOpen(true)}
                      variant="outline"
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create Custom Ratio
                    </Button>
                  </div>
                )}
              </TabsContent>
            ))}
          </ScrollArea>
        </Tabs>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          {onAddCustomRatio && (
            <Button
              variant="outline"
              onClick={onAddCustomRatio}
              className="w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Ratio
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 sm:flex-none">
              Save Selection ({totalSelected})
            </Button>
          </div>
        </DialogFooter>

          {/* Formula Builder Modal */}
        <FormulaBuilder 
          open={isFormulaBuilderOpen}
          onOpenChange={setIsFormulaBuilderOpen}
          availableFields={availableFields}
          existingCustomRatios={customRatios}
          onSave={handleAddCustomRatio}
          onDelete={(id) => {
              setCustomRatios(prev => prev.filter(r => r.id !== id));
              const newSet = new Set(selectedMetrics);
              newSet.delete(id);
              setSelectedMetrics(newSet);
          }}
      />

      </DialogContent>
    </Dialog>
  );
}

